import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';

import { Observable } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as fromAuth from '../modules/core/store/reducers/auth.reducers';
import * as fromApp from '../modules/core/store/app.state';
import * as storageHelper from '../modules/core/helpers/storage-helper';
import { AuthService } from '../services/auth.service';

export const SkipAuthInterceptorHeader = 'X-Skip-Auth-Interceptor';

/** Pass the request after adding authorization header. */
@Injectable()
export class AuthorizeRequestInterceptor implements HttpInterceptor {
  constructor(
    private store: Store<fromApp.AppState>,
    /**
     * AuthService is resolved lazily because it itself depends on HttpClient,
     * which would create a circular DI graph if we injected it directly.
     */
    private injector: Injector,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.store.select('auth').pipe(
      take(1),
      switchMap((authState: fromAuth.State) => {

        // Skip Adding Auth Headers When there is skip header
        if (req.headers.has(SkipAuthInterceptorHeader)) {
          const headers = req.headers.delete(SkipAuthInterceptorHeader);
          return next.handle(req.clone({ headers }));
        }

        // The refresh endpoint must not chain into itself, and pre-login
        // requests obviously have no token to attach or refresh.
        if (req.url.includes('entrance/refresh-login-token')
          || req.url.includes('entrance/login')) {
          return next.handle(req);
        }

        if (!authState.user) {
          return next.handle(req);
        }

        // Proactive refresh: if the access token is missing, expired (or
        // within the skew window) and the refresh token still has lifetime,
        // swap them out before sending the request. This saves the
        // 401-round-trip that the reactive refresh-token interceptor would
        // otherwise do — and covers the case where the page was just
        // reopened from localStorage with a long-lived refresh token but the
        // short-lived access token has already aged out.
        const accessUsable = !!authState.accessToken && !storageHelper.isJwtExpired(authState.accessToken);
        const refreshUsable = !!authState.refreshToken && !storageHelper.isJwtExpired(authState.refreshToken, 0);

        if (!authState.accessToken && !refreshUsable) {
          // No usable credentials at all — let the request go through
          // unauthenticated and let downstream guards / 401 handling react.
          return next.handle(req);
        }

        if (!accessUsable && refreshUsable) {
          const authService = this.injector.get(AuthService);
          return authService.refreshIfNeeded().pipe(
            switchMap(tokenData => {
              const fresh = req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + tokenData.accessToken),
              });
              return next.handle(fresh);
            }),
            // If the refresh itself fails (e.g. server rejects the refresh
            // token), fall through with the stale access token so the
            // RefreshTokenInterceptor's 401 handler can run its existing
            // logout flow — keeps the two interceptors' behaviour aligned.
            catchError(() => {
              const stale = req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + authState.accessToken),
              });
              return next.handle(stale);
            }),
          );
        }

        const secureReq = req.clone({
          headers: req.headers.set('Authorization', 'Bearer ' + authState.accessToken),
        });
        return next.handle(secureReq);
      })
    );
  }
}
